if (!customElements.get('product-hotspot')) {
  class ProductHotspot extends HTMLElement {
    // Phase durations for the staggered desktop segment-change animation.
    // CLOSE/FADE keep the CSS-defined easings (0.3s card width, 0.2s pin
    // opacity). The TRAVEL phase overrides the card transform and line
    // transitions inline so the glide lands with a pronounced easeOutQuint —
    // it's the moment that needs to feel deliberate.
    static CLOSE_MS = 300;
    static TRAVEL_MS = 500;
    static TRAVEL_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
    static FADE_MS = 200;

    // Fraction of viewport height the pin's anchor must scroll *above* the
    // bottom edge before the card opens. 0.15 ≈ pin anchor reaches the lower
    // 15% of the viewport. Keeps the entry-into-frame closed so the user sees
    // the card open in front of them rather than arriving already-expanded.
    static OPEN_TRIGGER_FROM_BOTTOM = 0.15;

    constructor() {
      super();
      this._onScroll = this._onScroll.bind(this);
      this._updateScroll = this._updateScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._ticking = false;
      this._isIntersecting = false;
      this._pinHandlers = new Map();
      this._activeSegment = null;
      this._activeIsOpen = false;
      this._pendingSegment = null;
      this._pendingShouldOpen = false;
      this._transitioning = false;
      this._timers = new Set();
    }

    connectedCallback() {
      this.pins = Array.from(this.querySelectorAll('[data-hotspot-pin]'));
      if (!this.pins.length) return;

      this._checkMobile();

      this._pinsByY = [...this.pins].sort(
        (a, b) => parseFloat(a.dataset.pinY || '50') - parseFloat(b.dataset.pinY || '50')
      );

      this._line = this.querySelector('.product-hotspot__line');

      this._wirePinEvents();
      this._wireScroll();

      document.addEventListener('keydown', this._onKeydown);
      window.addEventListener('resize', this._onResize, { passive: true });

      this._updateScroll();
    }

    disconnectedCallback() {
      this._unwirePinEvents();
      this._clearSchedule();
      if (this._observer) this._observer.disconnect();
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKeydown);
    }

    _checkMobile() {
      this._isMobile = window.matchMedia('(max-width: 749px)').matches;
    }

    _onResize() {
      const wasMobile = this._isMobile;
      this._checkMobile();
      if (wasMobile !== this._isMobile) {
        this._clearSchedule();
        this._activeSegment = null;
        this._activeIsOpen = false;
        this._closeAll();
        // Clear any lingering travel transforms / inline transitions from a
        // desktop transition that was interrupted by the resize.
        this.pins.forEach((pin) => {
          const card = pin.querySelector('.product-hotspot__card');
          if (card) this._snapResetTransform(card);
        });
        if (this._line) this._line.style.removeProperty('transition');
        if (this._isMobile) {
          this.pins.forEach((pin) => this._setVisible(pin, true));
        } else {
          this._updateScroll();
        }
      }
    }

    _wirePinEvents() {
      this.pins.forEach((pin) => {
        const card = pin.querySelector('.product-hotspot__card');
        if (!card) return;

        const onClick = (e) => {
          // Decide the mode at CLICK time, not from the connect/resize-cached
          // flag — zoom or emulation flips that miss a resize event would
          // otherwise let the tap fall through to the link. AAS-4096
          if (!window.matchMedia('(max-width: 749px)').matches) return;
          // Mobile pins don't expand into mini-cards (AAS-4096) — any pin
          // opens the section's featured-products modal (standard info-drawer
          // rendered by the section liquid), listing every pinned product as
          // a standard product tile. Desktop keeps the expanding cards.
          e.preventDefault();
          const modal = this.closest('section')?.querySelector('info-drawer[data-hotspot-modal]');
          if (modal && typeof modal.open === 'function') {
            modal.open(card);
            // Focus the tapped pin's product (AAS-4096): jump the scroller so
            // its tile leads the row — instantly, before the sheet finishes
            // sliding up, so it never reads as a scroll.
            const handle = card.dataset.productHandle;
            const tile = handle
              ? modal.querySelector(`.product-hotspot__modal-card[data-product-handle="${CSS.escape(handle)}"]`)
              : null;
            if (tile) tile.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
          }
        };

        card.addEventListener('click', onClick);

        this._pinHandlers.set(pin, { onClick, card });
      });
    }

    _unwirePinEvents() {
      this._pinHandlers.forEach(({ onClick, card }) => {
        card.removeEventListener('click', onClick);
      });
      this._pinHandlers.clear();
    }

    _wireScroll() {
      this._observer = new IntersectionObserver(
        (entries) => {
          this._isIntersecting = entries[0].isIntersecting;
          if (this._isIntersecting) this._updateScroll();
        },
        { rootMargin: '100px' }
      );
      this._observer.observe(this);
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    _onScroll() {
      if (this._isMobile || !this._isIntersecting || this._ticking) return;
      window.requestAnimationFrame(this._updateScroll);
      this._ticking = true;
    }

    _updateScroll() {
      this._ticking = false;

      if (this._isMobile) {
        this.pins.forEach((pin) => this._setVisible(pin, true));
        return;
      }

      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight;
      let progress = (vh - rect.top) / (rect.height + vh);
      progress = Math.max(0, Math.min(1, progress));

      const n = this._pinsByY.length;
      const desired = n === 1 ? 0 : Math.min(n - 1, Math.floor(progress * n));
      const shouldOpen = this._pinShouldOpen(this._pinsByY[desired]);

      this._requestSegment(desired, shouldOpen);
    }

    // Open the pin only once its anchor has scrolled comfortably into the
    // viewport. Avoids the first hotspot arriving on-screen already expanded
    // — instead it slides in closed and opens as the user scrolls in.
    _pinShouldOpen(pin) {
      const vh = window.innerHeight;
      return pin.getBoundingClientRect().top <= vh * (1 - ProductHotspot.OPEN_TRIGGER_FROM_BOTTOM);
    }

    _requestSegment(target, shouldOpen) {
      if (this._transitioning) {
        this._pendingSegment = target;
        this._pendingShouldOpen = shouldOpen;
        return;
      }
      if (this._activeSegment === target) {
        if (shouldOpen !== this._activeIsOpen) this._syncOpenState(shouldOpen);
        return;
      }
      this._transitionTo(target, shouldOpen);
    }

    _syncOpenState(shouldOpen) {
      if (this._activeSegment == null) return;
      const pin = this._pinsByY[this._activeSegment];
      if (shouldOpen && !pin.hasAttribute('data-open')) {
        this._openPin(pin);
      } else if (!shouldOpen && pin.hasAttribute('data-open')) {
        this._closePin(pin);
      }
      this._activeIsOpen = shouldOpen;
    }

    // Staggered desktop transition between segments:
    //   Phase 1 (CLOSE_MS):  collapse the currently-open card.
    //   Phase 2 (TRAVEL_MS): twin glide — the collapsed old card translates to
    //                        the new pin's anchor while the NEW card (also
    //                        collapsed, starting invisible at the old anchor)
    //                        travels in lockstep; the line slides in parallel.
    //                        At the flight's midpoint the two pins crossfade
    //                        (FADE_MS, centred on TRAVEL_MS/2), so the product
    //                        image swaps mid-travel and the card lands already
    //                        showing the next product. AAS-4096
    //   Phase 3:             at touchdown the new card's open expansion starts
    //                        (crossfade already settled in flight — landing
    //                        unlocks immediately, FADE_MS sooner than before).
    //                        If shouldOpen is false (pin not yet comfortably
    //                        in view), the card stays closed and a later
    //                        scroll tick opens it via _syncOpenState.
    // First paint (no previous segment) and reduced-motion users snap in.
    _transitionTo(target, shouldOpen) {
      const newPin = this._pinsByY[target];
      if (!newPin) return;

      const isFirstPaint = this._activeSegment == null;
      if (isFirstPaint || this._prefersReducedMotion()) {
        if (this._activeSegment != null) {
          this._closePin(this._pinsByY[this._activeSegment]);
        }
        this._pinsByY.forEach((pin, i) => {
          pin.style.opacity = i === target ? '1' : '0';
          pin.style.pointerEvents = i === target ? '' : 'none';
        });
        this._updateLineX(newPin);
        if (shouldOpen) this._openPin(newPin);
        this._activeIsOpen = !!shouldOpen;
        this._activeSegment = target;
        return;
      }

      const oldPin = this._pinsByY[this._activeSegment];
      const oldCard = oldPin.querySelector('.product-hotspot__card');
      const newCard = newPin.querySelector('.product-hotspot__card');
      const { dx, dy } = this._pinDelta(oldPin, newPin);
      this._transitioning = true;

      // Phase 1 — collapse old card.
      this._closePin(oldPin);

      this._schedule(ProductHotspot.CLOSE_MS, () => {
        // Phase 2 — twin glide. Translate the (now collapsed) old card to the
        // new pin's anchor; the new card mirrors the trip from the opposite
        // side (pre-positioned at the OLD anchor without transition, then
        // released to its own resting spot); the line slides in parallel.
        // Override the CSS-defined timing for all three so the glide uses the
        // longer travel duration and a stronger ease-out — the card's default
        // 0.3s easing is reserved for close/open shape changes.
        const travelCardTransition = `transform ${ProductHotspot.TRAVEL_MS}ms ${ProductHotspot.TRAVEL_EASING}`;
        const travelLineTransition = `left ${ProductHotspot.TRAVEL_MS}ms ${ProductHotspot.TRAVEL_EASING}, --line-y ${ProductHotspot.TRAVEL_MS}ms ${ProductHotspot.TRAVEL_EASING}`;
        if (oldCard) {
          oldCard.style.transition = travelCardTransition;
          oldCard.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
        }
        if (newCard) {
          newCard.style.transition = 'none';
          newCard.style.transform = `translate(calc(-50% + ${-dx}px), calc(-50% + ${-dy}px))`;
          void newCard.offsetWidth; // commit the start position un-animated
          newCard.style.transition = travelCardTransition;
          newCard.style.removeProperty('transform'); // release → glides to its own anchor
        }
        if (this._line) this._line.style.transition = travelLineTransition;
        this._updateLineX(newPin);

        // Mid-flight image swap (AAS-4096): crossfade the pins centred on the
        // travel midpoint — the pins' CSS opacity transition (FADE_MS) does the
        // blending, and both cards share the same in-flight position, so it
        // reads as one traveling card whose product swaps underway. The fade
        // fully settles before touchdown (midpoint + FADE_MS/2 < TRAVEL_MS).
        this._schedule((ProductHotspot.TRAVEL_MS - ProductHotspot.FADE_MS) / 2, () => {
          oldPin.style.opacity = '0';
          oldPin.style.pointerEvents = 'none';
          newPin.style.opacity = '1';
          newPin.style.pointerEvents = '';
        });

        this._schedule(ProductHotspot.TRAVEL_MS, () => {
          // Phase 3 — touchdown. The crossfade already settled in flight, so
          // just restore CSS-owned transitions (the inline travel transition
          // would otherwise suppress the width expansion) and open. The old
          // card is invisible — safe to snap-reset and unlock immediately.
          if (this._line) this._line.style.removeProperty('transition');
          if (newCard) newCard.style.removeProperty('transition');
          if (shouldOpen) this._openPin(newPin);
          this._activeIsOpen = !!shouldOpen;
          this._activeSegment = target;
          if (oldCard) this._snapResetTransform(oldCard);
          this._transitioning = false;

          // Drain any segment / open-state requested mid-transition.
          if (this._pendingSegment != null) {
            const next = this._pendingSegment;
            const nextOpen = this._pendingShouldOpen;
            this._pendingSegment = null;
            this._pendingShouldOpen = false;
            if (next !== target) {
              this._transitionTo(next, nextOpen);
            } else if (nextOpen !== this._activeIsOpen) {
              this._syncOpenState(nextOpen);
            }
          }
        });
      });
    }

    _pinDelta(fromPin, toPin) {
      const containerRect = this.getBoundingClientRect();
      const parsePct = (val, fallback) => {
        const n = parseFloat(val);
        return Number.isFinite(n) ? n : fallback;
      };
      const fromX = parsePct(fromPin.style.getPropertyValue('--pin-x'), 50);
      const toX = parsePct(toPin.style.getPropertyValue('--pin-x'), 50);
      const fromY = parsePct(fromPin.style.getPropertyValue('--pin-y'), 50);
      const toY = parsePct(toPin.style.getPropertyValue('--pin-y'), 50);
      return {
        dx: ((toX - fromX) / 100) * containerRect.width,
        dy: ((toY - fromY) / 100) * containerRect.height,
      };
    }

    _snapResetTransform(el) {
      el.style.transition = 'none';
      el.style.removeProperty('transform');
      // Force reflow so the change applies without animating.
      void el.offsetWidth;
      // Clear any inline transition entirely so the CSS-defined rule takes
      // over for subsequent (non-travel) animations.
      el.style.removeProperty('transition');
    }

    _schedule(ms, fn) {
      const id = setTimeout(() => {
        this._timers.delete(id);
        fn();
      }, ms);
      this._timers.add(id);
    }

    _clearSchedule() {
      this._timers.forEach((id) => clearTimeout(id));
      this._timers.clear();
      this._transitioning = false;
      this._pendingSegment = null;
      this._pendingShouldOpen = false;
    }

    _prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    // Align the decorative line's x/y to the given pin.
    // --line-x is a direct passthrough of the pin's horizontal %.
    // --line-y is converted from the pin's vertical % in the section to the
    // line element's local 0–100% (since the line is 300vh tall centered on
    // a 150vh section, the section occupies the line's 25%–75% range).
    _updateLineX(pin) {
      const pinX = pin.style.getPropertyValue('--pin-x');
      if (pinX) this.style.setProperty('--line-x', pinX);

      const pinY = parseFloat(pin.style.getPropertyValue('--pin-y'));
      if (Number.isFinite(pinY)) {
        this.style.setProperty('--line-y', `${25 + pinY * 0.5}%`);
      }
    }

    // Mobile-only visibility helper. Desktop uses _transitionTo for staged anims.
    _setVisible(pin, visible) {
      pin.style.opacity = visible ? '1' : '0';
      pin.style.pointerEvents = visible ? '' : 'none';
      if (!visible && pin.hasAttribute('data-open')) this._closePin(pin);
    }

    _openPin(pin) {
      const card = pin.querySelector('.product-hotspot__card');
      if (!card) return;

      this._applyEdgeShift(pin, card);
      pin.setAttribute('data-open', '');
      card.setAttribute('aria-expanded', 'true');

      const info = pin.querySelector('.product-hotspot__card-info');
      if (info) info.setAttribute('aria-hidden', 'false');
    }

    _closePin(pin) {
      pin.removeAttribute('data-open');
      const card = pin.querySelector('.product-hotspot__card');
      if (card) {
        card.setAttribute('aria-expanded', 'false');
        card.style.removeProperty('--card-shift');
      }
      const info = pin.querySelector('.product-hotspot__card-info');
      if (info) info.setAttribute('aria-hidden', 'true');
    }

    _closeAll() {
      this.pins.forEach((pin) => this._closePin(pin));
    }

    _applyEdgeShift(pin, card) {
      const containerRect = this.getBoundingClientRect();
      const pinRect = pin.getBoundingClientRect();
      const pinCenterX = pinRect.left + pinRect.width / 2 - containerRect.left;

      const expandedWidth = this._getExpandedWidth();
      const half = expandedWidth / 2;
      const margin = 8;
      let shift = 0;
      if (pinCenterX - half < margin) {
        shift = margin + half - pinCenterX;
      } else if (pinCenterX + half > containerRect.width - margin) {
        shift = containerRect.width - margin - half - pinCenterX;
      }
      card.style.setProperty('--card-shift', `${shift}px`);
    }

    // Mirrors the CSS `--card-expanded` clamp() in product-hotspot.css.
    // Keep in sync if the clamp values change.
    _getExpandedWidth() {
      const vw = window.innerWidth;
      if (this._isMobile) return Math.max(200, Math.min(280, 0.55 * vw));
      return Math.max(240, Math.min(380, 0.22 * vw));
    }

    _onKeydown(e) {
      if (e.key !== 'Escape') return;
      const openPin = this.querySelector('[data-hotspot-pin][data-open]');
      if (!openPin) return;
      this._closeAll();
      const card = openPin.querySelector('.product-hotspot__card');
      if (card) card.focus();
    }
  }

  customElements.define('product-hotspot', ProductHotspot);
}
